import React, { FunctionComponent, useState, useMemo, useEffect, useRef } from 'react';
import { useMasterData } from 'hooks';
import clsx from 'clsx';
import styles from './FoodQuantity.module.scss';
import SelectComponent from '../SelectComponent';
import { SelectOption } from '../SelectComponent/SelectComponent';
import EditableCounterField from '../EditableCounterField';
import {
    getStepForUnit,
    getMaxDecimalsForUnit,
    isValidDecimalPlaces,
    getCupQuarterFractionTooltip,
    isQuarterEquivalent
} from './foodQuantityUnitsConfig';
import { DrawerTooltip } from '../../DrawerTooltip';

interface FoodQuantityProps {
    onQuantityChange: (quantity: number, unit: number) => void;
    selectedQuantity?: number;
    selectedUnit?: number;
    className?: string;
}

const FoodQuantity: FunctionComponent<FoodQuantityProps> = props => {
    const foodUnits = useMasterData().foodQuantityUnits;
    const [quantity, setQuantity] = useState<number | undefined>(props.selectedQuantity);
    const [selectedUnit, setSelectedUnit] = useState<number | undefined>(props.selectedUnit);
    const [decimalError, setDecimalError] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [triggerWidth, setTriggerWidth] = useState<number>(0);

    const selectedUnitName = useMemo(() => {
        const unit = foodUnits.find(u => u.id === selectedUnit);
        return unit?.name ?? '';
    }, [foodUnits, selectedUnit]);

    const step = useMemo(() => getStepForUnit(selectedUnitName), [selectedUnitName]);
    const maxDecimals = useMemo(
        () => (selectedUnitName ? getMaxDecimalsForUnit(selectedUnitName) : undefined),
        [selectedUnitName]
    );

    const validateQuantity = (value: number): boolean => {
        if (selectedUnitName == null || maxDecimals === undefined) {
            setDecimalError(null);
            return true;
        }
        if (isValidDecimalPlaces(value, maxDecimals)) {
            setDecimalError(null);
            return true;
        }
        setDecimalError(
            `Maximum ${maxDecimals} decimal place${maxDecimals === 1 ? '' : 's'} allowed for ${selectedUnitName}.`
        );
        return false;
    };

    const handleUnitChange = (option: SelectOption) => {
        const unitId = option.id as number;
        const unitName = (option.name ?? '') as string;
        setSelectedUnit(unitId);
        const newMaxDecimals = unitName ? getMaxDecimalsForUnit(unitName) : undefined;
        if (quantity != null && newMaxDecimals !== undefined) {
            if (isValidDecimalPlaces(quantity, newMaxDecimals)) {
                setDecimalError(null);
            } else {
                setDecimalError(
                    `Maximum ${newMaxDecimals} decimal place${newMaxDecimals === 1 ? '' : 's'} allowed for ${unitName}.`
                );
            }
        } else {
            setDecimalError(null);
        }
        props.onQuantityChange(quantity ?? 0, unitId);
    };

    useEffect(() => {
        if (quantity != null && selectedUnitName && maxDecimals !== undefined) {
            if (isValidDecimalPlaces(quantity, maxDecimals)) {
                setDecimalError(null);
            } else {
                setDecimalError(
                    `Maximum ${maxDecimals} decimal place${
                        maxDecimals === 1 ? '' : 's'
                    } allowed for ${selectedUnitName}.`
                );
            }
        } else {
            setDecimalError(null);
        }
    }, [quantity, selectedUnitName, maxDecimals]);

    const getUnitOptions = (): SelectOption[] => {
        return foodUnits.map(unit => ({
            id: unit.id,
            name: unit.name
        }));
    };

    const getSelectedUnitOption = (): SelectOption | undefined => {
        const unit = foodUnits.find(u => u.id === selectedUnit);
        return unit ? { id: unit.id, name: unit.name } : undefined;
    };

    const handleQuantityChange = (value: number) => {
        setQuantity(value);
        validateQuantity(value);
        props.onQuantityChange(value, selectedUnit ?? 0);
    };

    const isCup = selectedUnitName.trim().toLowerCase() === 'cup';
    const cupFractionTooltip =
        isCup && quantity != null && isQuarterEquivalent(quantity) && !Number.isInteger(quantity)
            ? getCupQuarterFractionTooltip(quantity)
            : null;

    useEffect(() => {
        if (!cupFractionTooltip) {
            setTriggerWidth(0);
            return;
        }
        const el = containerRef.current;
        if (!el) return;
        const updateWidth = () => setTriggerWidth(el.offsetWidth);
        updateWidth();
        const ro = new ResizeObserver(updateWidth);
        ro.observe(el);
        return () => ro.disconnect();
    }, [cupFractionTooltip]);

    return (
        <div className={clsx(styles.foodQuantityContainer, props.className)}>
            <label className={styles.label}>
                Food Qty<span className={styles.required}>*</span>
            </label>
            <div className={styles.inputGroup}>
                <div
                    ref={containerRef}
                    className={styles.quantityInputContainer}
                >
                    {cupFractionTooltip ? (
                        <DrawerTooltip
                            content={cupFractionTooltip}
                            placement="top"
                        >
                            <div
                                className={styles.quantityInputWrapper}
                                style={triggerWidth > 0 ? { width: triggerWidth } : undefined}
                            >
                                <EditableCounterField
                                    className={styles.quantityInput}
                                    value={quantity}
                                    minLimit={0}
                                    step={step}
                                    onChange={handleQuantityChange}
                                    placeholder="Ex. 1.5"
                                />
                            </div>
                        </DrawerTooltip>
                    ) : (
                        <EditableCounterField
                            className={styles.quantityInput}
                            value={quantity}
                            minLimit={0}
                            step={step}
                            onChange={handleQuantityChange}
                            placeholder="Ex. 1.5"
                        />
                    )}
                </div>
                <div
                    className={styles.divider}
                    aria-hidden="true"
                />
                <div className={styles.unitSelector}>
                    <SelectComponent
                        selectedOption={getSelectedUnitOption()}
                        options={getUnitOptions()}
                        onSelect={handleUnitChange}
                        placeholder="Select unit"
                        className={styles.dropdownContainer}
                        borderStyle={styles.foodQtyBorder}
                        borderExpandedStyle={styles.foodQtyBorderExpanded}
                    />
                </div>
            </div>
            {decimalError && (
                <p
                    className={styles.errorMessage}
                    role="alert"
                >
                    {decimalError}
                </p>
            )}
        </div>
    );
};

export default FoodQuantity;
